import { useState } from "react";
import { Box, Chip, MenuItem, Select, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { RevisionFilterState, RevisionRange } from "../types";
import { DATE_VALUE_FORMAT, defaultCustomRange } from "../utils";
import { tokens } from "../tokens";

interface Props {
  filters: RevisionFilterState;
  editors: string[];
  onChange: (filters: RevisionFilterState) => void;
}

const RANGES: { value: RevisionRange; label: string; tracker: string }[] = [
  { value: "today", label: "Today", tracker: "1080" },
  { value: "yesterday", label: "Yesterday", tracker: "1081" },
  { value: "7d", label: "Last 7 days", tracker: "1082" },
  { value: "30d", label: "Last 30 days", tracker: "1083" },
  { value: "custom", label: "Custom range", tracker: "1084" },
];

const datePickerFieldSx = {
  width: 160,
  "& .MuiOutlinedInput-root": {
    borderRadius: tokens.radius,
    fontSize: 12,
    backgroundColor: tokens.bg,
    cursor: "pointer",
    "& fieldset": { borderColor: tokens.border },
    "&:hover fieldset": { borderColor: tokens.borderStrong },
    "&.Mui-focused fieldset": {
      borderColor: tokens.accent,
      borderWidth: "1px",
    },
  },
  "& .MuiOutlinedInput-input": { padding: "6px 9px", cursor: "pointer" },
};

const toDayjs = (value: string): Dayjs | null =>
  value ? dayjs(value, DATE_VALUE_FORMAT) : null;

const RevisionFilters = ({ filters, editors, onChange }: Props) => {
  // Controlled so the picker can also be opened by clicking the field itself,
  // not just the calendar icon.
  const [fromOpen, setFromOpen] = useState(false);
  const [toOpen, setToOpen] = useState(false);

  const handleRangeClick = (range: RevisionRange) => {
    // Entering the custom range seeds it with the last month.
    if (range === "custom" && filters.range !== "custom") {
      onChange({ ...filters, range, ...defaultCustomRange() });
      return;
    }
    onChange({ ...filters, range });
  };

  const handleDateChange =
    (key: "customFrom" | "customTo") => (value: Dayjs | null) => {
      if (!value || !value.isValid()) return;
      onChange({ ...filters, [key]: value.format(DATE_VALUE_FORMAT) });
    };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        flexWrap: "wrap",
        padding: "14px 22px",
        borderBottom: `1px solid ${tokens.border}`,
      }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
      >
        {RANGES.map((range) => {
          const active = filters.range === range.value;
          return (
            <Chip
              key={range.value}
              label={range.label}
              clickable
              onClick={() => handleRangeClick(range.value)}
              data-tracker={range.tracker}
              sx={{
                height: 30,
                fontSize: 12,
                fontWeight: 600,
                borderRadius: 999,
                border: `1px solid ${active ? tokens.accent : tokens.borderStrong}`,
                backgroundColor: active ? tokens.accent : tokens.surface,
                color: active ? "#FFFFFF" : tokens.textMuted,
                "&:hover": {
                  backgroundColor: active
                    ? tokens.accentHover
                    : tokens.accentSoft,
                  borderColor: tokens.accent,
                  color: active ? "#FFFFFF" : tokens.accent,
                },
              }}
            />
          );
        })}

        {filters.range === "custom" && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, ml: 0.5 }}
            >
              <DatePicker
                value={toDayjs(filters.customFrom)}
                onChange={handleDateChange("customFrom")}
                format="DD/MM/YYYY"
                /* Start date can never be after the end date. */
                maxDate={toDayjs(filters.customTo) ?? undefined}
                open={fromOpen}
                onOpen={() => setFromOpen(true)}
                onClose={() => setFromOpen(false)}
                slotProps={{
                  // Value is picked, never typed.
                  field: { readOnly: true },
                  textField: {
                    size: "small",
                    onClick: () => setFromOpen(true),
                    inputProps: { "aria-label": "Start date" },
                    sx: datePickerFieldSx,
                  },
                }}
              />
              <Typography sx={{ fontSize: 12, color: tokens.textFaint }}>
                to
              </Typography>
              <DatePicker
                value={toDayjs(filters.customTo)}
                onChange={handleDateChange("customTo")}
                format="DD/MM/YYYY"
                /* End date can never be before the start date. */
                minDate={toDayjs(filters.customFrom) ?? undefined}
                open={toOpen}
                onOpen={() => setToOpen(true)}
                onClose={() => setToOpen(false)}
                slotProps={{
                  field: { readOnly: true },
                  textField: {
                    size: "small",
                    onClick: () => setToOpen(true),
                    inputProps: { "aria-label": "End date" },
                    sx: datePickerFieldSx,
                  },
                }}
              />
            </Box>
          </LocalizationProvider>
        )}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: tokens.textFaint,
            whiteSpace: "nowrap",
          }}
        >
          Modified by
        </Typography>
        <Select
          value={filters.modifiedBy}
          displayEmpty
          onChange={(event) =>
            onChange({ ...filters, modifiedBy: event.target.value })
          }
          sx={{
            minWidth: 180,
            fontSize: 12,
            borderRadius: tokens.radius,
            backgroundColor: tokens.bg,
            "& .MuiOutlinedInput-input": { padding: "7px 11px" },
            "& fieldset": { borderColor: tokens.border },
          }}
        >
          <MenuItem value="" sx={{ fontSize: 12 }}>
            All editors
          </MenuItem>
          {editors.map((editor) => (
            <MenuItem key={editor} value={editor} sx={{ fontSize: 12 }}>
              {editor}
            </MenuItem>
          ))}
        </Select>
      </Box>
    </Box>
  );
};

export default RevisionFilters;
